import * as React from "react"

import { cn } from "@/lib/utils"

/**
 * Container width variants for different page layouts.
 * All variants are centered with auto margins.
 */
const widthVariants = {
  // Full width with padding (for dashboards, wide content)
  full: "w-full",
  // Wide container (for wider layouts)
  wide: "w-full lg:w-11/12 xl:w-10/12 2xl:max-w-7xl",
  // Default container (75% on 2xl+, 100% below for collapsed sidebar)
  default: "w-full 2xl:w-3/4",
  // Narrow container (for forms, settings)
  narrow: "w-full max-w-3xl",
  // Compact container (for login, small forms)
  compact: "w-full max-w-xl",
} as const

const paddingVariants = {
  none: "p-0",
  sm: "px-4 py-4 sm:px-6 sm:py-6",
  md: "px-4 py-6 sm:px-6 sm:py-8 lg:px-8",
  lg: "px-4 py-8 sm:px-8 sm:py-10 lg:px-12",
} as const

export interface PageContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  fullHeight?: boolean
  /** Container max-width variant */
  width?: keyof typeof widthVariants
  /** Padding variant */
  padding?: keyof typeof paddingVariants
}

export function PageContainer({
  className,
  fullHeight,
  width = "default",
  padding = "md",
  ...props
}: PageContainerProps) {
  return (
    <div
      className={cn(
        "min-h-full mx-auto space-y-6",
        widthVariants[width],
        paddingVariants[padding],
        fullHeight &&
          "h-[calc(100vh-4rem)] flex flex-col relative overflow-hidden",
        className
      )}
      {...props}
    />
  )
}
